import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 6.1 visual preview at /preview/lesson/6.1
 * Week 6: "Build and ship something that works"
 * Week 6 is a single build lesson: no vocab, no five-lesson arc.
 * Students pick a platform (Gemini Notebook / Perplexity Space / Claude Project / ElevenLabs Agent),
 * build a working AI tool, audit it, and write a 600-1,000 word case study.
 */

export default function LessonPreviewPage6_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-map',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <Map />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 6 · Build and ship something that works · Lesson 6.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Ship a working AI tool. Write the case study. Prove you can build.
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 6 sessions of 30 minutes each
        </span>
        <span className="text-line">|</span>
        <span>Threads: Fluency · Judgment · Character</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        A shipped tool with a link. A 600-1,000 word case study. Both by end of week.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="Two students. Same week. One writes about a tool. One builds it.">
      <p className="mt-6 text-text/90">
        Two students are sitting in the same coffee shop, both working on
        the same Week 6 assignment. Student A has spent the week writing a
        document about the AI tool they would build if they had time. It
        is well-researched. It has diagrams. It quotes three sources. When
        they turn it in, it will look impressive. Student B has spent the
        week building the actual tool. A working link. Something you
        could open right now and use. Their document is shorter. It is a
        case study of what they built, what broke, and what they would
        do next.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Student A · Wrote about the tool
          </p>
          <p className="mt-3 text-text/90 italic">
            Ten pages, three diagrams, five sources. Reads well. Six months
            from now, when they apply to something, they will describe a
            tool they never made. The document proves they can research
            and write. It does not prove they can ship.
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Student B · Built the tool
          </p>
          <p className="mt-3 text-text/90 italic">
            One working link. A short case study explaining the problem,
            the design, what broke, what they learned. Six months from
            now, they can say "I made this. Here is the link. Here is
            what I learned." That sentence changes what conversations they
            can have.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Pick one of four platforms. Build a working AI tool that solves
          a specific problem for a specific person. Audit it. Write the
          case study. Send the link to one real reader by the end of the
          week.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="What" value="A working AI tool at a shareable URL, plus a 600-1,000 word case study." />
          <BriefRow label="Where" value="One of four free platforms: Gemini Notebook, Perplexity Space, Claude Project, or ElevenLabs Agent." />
          <BriefRow label="For" value="One specific person with a real problem. Not a general audience. Not you." />
          <BriefRow label="Ship" value="Link to a stranger by end of week. Case study attached. Both live." />
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          This week has one lesson. This one. No new vocab, no new
          concepts. Everything you learned in weeks 1 through 5 is what
          you need. The whole week is about using it to build something
          that actually works.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

/* --- Section 2: Map --- */
function Map() {
  return (
    <Section id="sec-map" kicker="The map" title="Four platforms. All free to start. All shareable by URL. Pick one.">
      <p className="mt-6 text-text/90">
        We picked these four because they all have a free plan, they are
        all shareable by a link, and none of them needs an install. Three
        are text tools you type into. The fourth is a voice agent people
        talk to out loud. Each one is best at a different kind of problem.
        The choice comes from what you are actually trying to solve.
      </p>
      <PlatformDiagram />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          How to pick in one question
        </p>
        <p className="mt-3 text-text/90 leading-relaxed">
          Ask yourself: <span className="text-amber">what is the specific problem you are solving, and who is it for?</span>
        </p>
        <ul className="mt-4 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            If the answer is "I want to help someone learn something specific," build a <span className="text-amber">Gemini Notebook</span>.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            If the answer is "I want to answer an ongoing question well every time," build a <span className="text-amber">Perplexity Space</span>.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            If the answer is "I want to do this task consistently every time," build a <span className="text-amber">Claude Project</span>.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            If the answer is "I want someone to practice or get help by talking it through out loud," build an <span className="text-amber">ElevenLabs Agent</span>.
          </li>
        </ul>
        <p className="mt-4 text-sm text-muted italic">
          If you cannot answer the question, you are not ready to build
          yet. Sharpen the problem before you pick a platform.
        </p>
      </div>
    </Section>
  );
}

const PLATFORMS = [
  {
    n: '01',
    name: 'Gemini Notebook',
    url: 'https://notebook.google.com/',
    urlLabel: 'notebook.google.com',
    accent: 'emerald' as const,
    tagline: 'Turn source material into a subject-matter expert.',
    what: 'You upload PDFs, articles, class notes, videos. Gemini Notebook reads them and becomes an expert on that material. It answers questions, generates study guides, and can even produce a podcast episode about your sources.',
    bestFor: 'Problems that sound like "I want to help someone learn X."',
    examples: [
      'A tutor for a class you are taking, loaded with textbook chapters and class notes.',
      'A study assistant for the SAT, AP exam, or a certification you are working toward.',
      'A research assistant on a company, a cause, or a historical period.',
      'A guide for a hobby, loaded with reference material you have gathered.',
    ],
    doesNotDo: 'Take action. It reads and answers. It does not send emails, book things, or run workflows.',
  },
  {
    n: '02',
    name: 'Perplexity Space',
    url: 'https://perplexity.ai',
    urlLabel: 'perplexity.ai',
    accent: 'amber' as const,
    tagline: 'A curated research assistant with custom instructions and trusted sources.',
    what: 'You configure how it behaves, what tone it uses, and what it should always check. Every question runs through your instructions and against sources you have chosen. It is a research assistant that stays consistent across weeks.',
    bestFor: 'Problems that sound like "I keep looking this up and I want it done well every time."',
    examples: [
      'A college research Space that compares schools by the criteria you actually care about.',
      'A daily briefing Space that catches you up on a field or news area.',
      'A homework helper Space with instructions that force it to show its work.',
      'A shopping research Space that compares products the way your family actually shops.',
    ],
    doesNotDo: 'Deep expertise on one static body of material. For that, use Gemini Notebook.',
  },
  {
    n: '03',
    name: 'Claude Project',
    url: 'https://claude.ai',
    urlLabel: 'claude.ai',
    urlNote: '(18+ required)',
    accent: 'emerald' as const,
    tagline: 'A custom AI teammate with knowledge loaded in and instructions on how to work.',
    what: 'You give Claude context, examples, and rules once. It applies them every time you use it. Great for repeatable tasks where you want the same standard applied consistently.',
    bestFor: 'Problems that sound like "I do this task over and over and want it done consistently."',
    examples: [
      'A writing coach that knows your voice and pushes back on drafts the way a real editor would.',
      'A homework debugger for a specific class that only helps you find your own mistakes.',
      'A planning assistant that turns rough notes into structured plans in your format.',
      'An interview prep assistant loaded with the job description and your resume.',
    ],
    doesNotDo: 'Handle huge source material as well as Gemini Notebook. For that, use Gemini Notebook.',
  },
  {
    n: '04',
    name: 'ElevenLabs Agent',
    url: 'https://elevenlabs.io/agents',
    urlLabel: 'elevenlabs.io/agents',
    urlNote: '(18+ required)',
    accent: 'amber' as const,
    tagline: 'A voice agent people talk to out loud, like a phone call.',
    what: 'You pick a voice, write the first thing it says, give it instructions, and load a short knowledge base. People open your link, press call, and have a spoken conversation with it. It listens, answers out loud, and follows the steps you designed.',
    bestFor: 'Problems that sound like "someone needs to practice or get help by talking, not typing."',
    examples: [
      'A mock interviewer that asks real questions for a job or program and gives feedback on each answer.',
      'A conversation partner for the language you are learning, set to your level.',
      'A spoken FAQ for a family business, club, or team that answers the questions people call about.',
      'A rehearsal audience that asks follow-up questions after you practice a presentation.',
    ],
    doesNotDo: 'Deep research or long documents. For those, use one of the other three. The free plan includes 15 minutes of calls a month, so write your test conversations before you start talking.',
  },
];

function PlatformDiagram() {
  return (
    <div className="mt-6 space-y-4">
      {PLATFORMS.map((p) => {
        const isEm = p.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={p.n} className={`rounded-xl border ${borderCls} ${bgCls} p-6`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-mono text-xs text-muted">{p.n}</span>
                <h4 className={`text-xl font-semibold ${labelCls}`}>{p.name}</h4>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`font-mono text-[11px] underline ${labelCls} hover:opacity-80`}
                >
                  {p.urlLabel} ↗
                </a>
                {'urlNote' in p && p.urlNote ? (
                  <span className="font-mono text-[11px] text-muted">
                    {p.urlNote}
                  </span>
                ) : null}
              </div>
            </div>
            <p className="mt-2 text-sm italic text-text/85">{p.tagline}</p>
            <p className="mt-4 text-sm text-text/90">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted mr-2">
                What it is
              </span>
              {p.what}
            </p>
            <p className="mt-3 text-sm text-text/90">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted mr-2">
                Best for
              </span>
              {p.bestFor}
            </p>
            <div className="mt-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
                What you might build
              </p>
              <ul className="mt-2 space-y-1.5 text-sm text-text/85">
                {p.examples.map((ex, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={`font-mono text-[10px] ${labelCls} shrink-0 mt-1`}>→</span>
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-4 text-sm text-muted italic">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted mr-2">
                What it does not do
              </span>
              {p.doesNotDo}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Pick your problem and platform --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Pick your problem and pick your platform" title="One sentence. One user. One platform. In that order.">
      <p className="mt-6 text-text/90">
        Before you open anything, get the problem tight. Most students who
        fail this capstone fail here: they pick a topic instead of a
        problem, so nothing they build has a user in mind. A topic is
        "AI for a class I am taking." A problem is "This helps students
        prep for the AP US History exam by turning their teacher's slides
        into practice questions with explanations." One has a user. The
        other is a folder name.
      </p>

      <StageBlock
        label="The one-sentence problem statement"
        accent="emerald"
        body="Write one sentence in this shape: &ldquo;This helps [specific user] do [specific thing] by [specific method].&rdquo; If you cannot fill any of the three blanks with something concrete, keep working the sentence. The sharper the sentence, the easier every later choice becomes. If your sentence has the word &lsquo;anyone&rsquo; or &lsquo;users&rsquo; or &lsquo;people,&rsquo; it is not sharp yet."
      />

      <StageBlock
        label="Match the problem to a platform"
        accent="amber"
        body="Read your sentence back. Which of the four platforms fits? If the specific thing is &lsquo;learn&rsquo; or &lsquo;study&rsquo; or &lsquo;understand,&rsquo; try Gemini Notebook. If it is &lsquo;research&rsquo; or &lsquo;compare&rsquo; or &lsquo;stay current on,&rsquo; try Perplexity Space. If it is &lsquo;write,&rsquo; &lsquo;plan,&rsquo; &lsquo;draft,&rsquo; or &lsquo;debug&rsquo; the same kind of thing over and over, try Claude Project. If it is &lsquo;practice,&rsquo; &lsquo;rehearse,&rsquo; or &lsquo;talk through&rsquo; out loud, try an ElevenLabs Agent. When it is close between two, pick the one that requires less setup. You can always rebuild on the other one later."
      />

      <StageBlock
        label="The prompt you give AI at this stage"
        accent="emerald"
        body="&ldquo;Here is my one-sentence problem statement: [your sentence]. Push back on it. Where is it vague? Where is the user still generic? What is one specific example of this user I have not named yet? Do not rewrite the sentence. Do not suggest a platform yet.&rdquo; AI is a mirror on the sharpness of your problem. The mirror does not pick for you. You do."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One sentence problem statement, specific enough to name the user, the thing, and the method.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One platform chosen. One reason written down for why that platform and not the other three.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            An empty project created on the platform. You are not building yet. You just need the container open.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Section 4: Stage 2 Build the first version --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Build the first version" title="Load the sources. Write the instructions. Run three real tasks.">
      <p className="mt-6 text-text/90">
        The first version is not the final version. Get something working
        end to end before you polish anything. On Gemini Notebook, that means
        upload your sources and ask three questions. On Perplexity Space,
        that means write custom instructions and ask three real questions.
        On Claude Project, that means load your context and run three real
        tasks. On an ElevenLabs Agent, that means write the instructions and
        first message, then hold three real conversations. In every case, the goal is to see the tool respond to
        something real before you spend more time on it.
      </p>

      <StageBlock
        label="Load what the tool needs to know"
        accent="amber"
        body="For Gemini Notebook: upload the 3-10 sources the tool should be an expert on. Actual PDFs, articles, notes, transcripts. Not links to search-and-summarize later. The tool is only as good as what you give it. For Perplexity Space: write the custom instructions, pick the sources, set the tone. For Claude Project: write the project instructions and load reference documents that show Claude how you want tasks done. For ElevenLabs Agent: write the system prompt (who the agent is, who it is talking to, what it must never do), write the first message it says, pick a voice that fits the user, and load a short knowledge base. Keep spoken answers short. Nobody wants to listen to a paragraph. In all four cases, be specific. Vague instructions produce vague tools."
      />

      <StageBlock
        label="Run three tasks it should handle well"
        accent="emerald"
        body="Pick three tasks a real user would give this tool. Not test questions. Real ones your target user actually has. Run each of them. Read what the tool returns. Do not fix anything yet. You are looking at the first honest picture of what your tool does out of the box. Write down for each task: what worked, what did not, what was surprising. This is the raw material for Stage 3."
      />

      <StageBlock
        label="Run one edge case it should refuse or flag"
        accent="amber"
        body="Pick one task the tool should not do. Something outside its scope. Something a user might reasonably ask that would be a bad idea to answer. Run it. See what happens. A good tool refuses, redirects, or flags. A bad tool cheerfully answers something it should not. The edge case is where you find out whether the tool has judgment or just fluency. On a voice agent, also talk over it, say one word unclearly, and ask it something off-topic. Listen for whether it recovers or makes something up. Write down what happened."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A first-version tool loaded with real sources or instructions.
          Three real tasks run through it, results written down. One edge
          case run through it, result written down. You now know what the
          tool actually does, not what you hoped it would do.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Audit and fix --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Self-audit and revise" title="Fix what broke. Cut what does not fit. Do not smooth over what surprised you.">
      <p className="mt-6 text-text/90">
        This is the stage most students skip. Do not skip it. The self-audit
        is where the tool becomes good. It is also where the case study
        section 3 comes from, so honest notes here save you time later.
        You built the tool. You are the first user. You are also the
        first auditor.
      </p>

      <StageBlock
        label="Compare what happened to what you wanted"
        accent="emerald"
        body="Take your three task results and your edge case result. For each one, write two things: what the tool got right, and what it got wrong or missed. Be specific. &ldquo;It hallucinated a source&rdquo; is more useful than &ldquo;it was inaccurate.&rdquo; &ldquo;It refused the edge case but did not say why&rdquo; is more useful than &ldquo;it worked.&rdquo; If your notes are vague, your fixes will be vague."
      />

      <StageBlock
        label="Change the sources, instructions, or scope"
        accent="amber"
        body="Now revise. Add missing sources. Rewrite fuzzy instructions. Narrow the scope if the tool is trying to do too much. Widen the scope if the tool is refusing to help with something it should. Rerun the tasks that failed. If they still fail, keep revising until they work, or until you understand why they will never work with this platform. Both outcomes are useful."
      />

      <StageBlock
        label="The prompt that helps at this stage"
        accent="emerald"
        body="&ldquo;Here is my one-sentence problem statement, my sources or instructions, and the three task results plus the edge case. Where are my sources or instructions pulling the tool away from the problem? What is one specific change I should make before shipping? Do not rewrite the whole thing.&rdquo; AI helps you see the gap between what you built and what you meant to build. You make the fixes."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A revised tool. Three tasks that now work the way you meant them
          to. One edge case handled honestly (refused, redirected, or
          flagged). Notes on what changed between version 1 and version 2,
          and why. These notes become section 3 of the case study.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Case study and ship --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Write the case study, ship the link" title="Four sections. 600-1,000 words. One link to a real reader today.">
      <p className="mt-6 text-text/90">
        The case study is a professional writing sample that proves you
        can build something, audit it honestly, and think clearly about
        it afterward. Use the longform structure you already know: clear
        opening, one strong section per idea, one honest section on what
        broke. Do not write marketing copy. Words like
        &ldquo;seamless,&rdquo;
        &ldquo;revolutionary,&rdquo; and &ldquo;game-changing&rdquo; mean
        you are covering something up. Cut them. Write what actually
        happened.
      </p>

      <CaseStudyStructure />

      <StageBlock
        label="The audit read"
        accent="emerald"
        body="Read the whole case study aloud. Anywhere you sound like a press release, rewrite it as a sentence you would say to one specific person. Anywhere you skipped over a real thing that broke, add the specifics back in. Section 3 is where most drafts get soft. Push back on your own draft: what actually broke, what actually surprised you, what did you actually change?"
      />

      <StageBlock
        label="Ship"
        accent="amber"
        body="Confirm your tool link works in a browser you are not logged into. If the link is private by default, change the sharing settings so a stranger can open it. For an ElevenLabs Agent, use the agent&rsquo;s shareable link, make sure authentication is turned off so a stranger can start a call, and have the agent say up front that it is an AI. Every call uses your free minutes, so tell your reader to keep it to a few minutes. Send the link and the case study to one real reader today: a mentor, a teacher, a program director, a hiring manager, an editor, a friend who tells the truth. The whole point of the capstone is that the tool reaches someone outside your head. The first send is the hardest. The second one will be easier."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A live tool at a shareable URL that a stranger can open. A
          600-1,000 word case study in your voice, with an honest section
          3. One real reader who received both today. One sentence in
          your notes about what felt hardest: the problem statement, the
          build, the audit, or the send.
        </p>
      </div>
    </Section>
  );
}

const CASE_STUDY_SECTIONS = [
  {
    n: '01',
    name: 'The problem',
    words: '150-200 words',
    body: 'Who has this problem? How do they experience it? Why does it matter? What are they doing right now to solve it, and why is that not enough? Name a specific user, not a category. If you cannot name a real person or a real type of person you have met, the problem is still too abstract.',
  },
  {
    n: '02',
    name: 'The design',
    words: '200-300 words',
    body: 'What platform did you choose and why. What sources or instructions did you give it. What did you decide to leave out. Walk through one specific choice you made and the alternative you rejected. The reader learns more from one specific choice than from a list of features.',
  },
  {
    n: '03',
    name: 'What you found when you audited it',
    words: '150-250 words',
    body: 'You ran your own tool through three tasks and one edge case. What did it get right? What did it get wrong? What did you have to change? Be specific. Do not smooth this section over. The point is what you learned. If this section says everything worked great, you did not actually audit it.',
  },
  {
    n: '04',
    name: 'What you would do next',
    words: '100-200 words',
    body: 'If you had another week, what would you build on top of this? What is the version 2? What would you want to test? This is where the reader sees whether you can think about your own work honestly. Vague plans mean you have not thought hard enough about what you shipped.',
  },
];

function CaseStudyStructure() {
  return (
    <div className="mt-6 space-y-3">
      {CASE_STUDY_SECTIONS.map((s) => (
        <div key={s.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">{s.n}</span>
            <h4 className="text-base font-semibold text-amber">{s.name}</h4>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
              {s.words}
            </span>
          </div>
          <p className="mt-2 text-sm text-text/85">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

/* --- Section 7: Carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Five habits to keep. Five traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Start with the specific user. If you cannot name one, the
              problem is not sharp enough to build for.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Ship the ugly first version before you polish. You cannot
              audit what does not exist.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              Run three real tasks plus one edge case. That is what an
              audit means. Not a self-review that says it looks good.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">04</span>
              Write the case study in your voice, not press-release voice.
              The reader trusts specifics.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">05</span>
              Send the link. A tool that lives only on your account is not
              shipped.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              Building for &ldquo;anyone.&rdquo; A tool for everyone is a
              tool for no one.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              Writing about the tool instead of building it. Student A's
              trap. Impressive-looking. Not shipped.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              Skipping the self-audit because &ldquo;it seems fine.&rdquo;
              This is where the learning is. Do not skip.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">04</span>
              A case study that reads like marketing. Cut every word your
              user would not use in real conversation.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">05</span>
              Confusing shipped with perfect. Shipped means a stranger can
              open it. That is enough. Version 2 comes later.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          Six weeks ago, you had not built anything with AI that a stranger
          could open and use. Today, you have a working tool at a URL and
          a case study that proves you can think about what you built. AI
          helped you draft, load sources, and audit. AI did not pick the
          problem, did not name the user, did not decide what to cut. That
          judgment is yours. It is also the part employers, admissions
          readers, and program directors cannot get from a transcript.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 6" title="Screenshot this. The build week on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 6 · Lock card · Build and ship something that works
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Pick a real problem for a real user. Build the tool. Audit it
          honestly. Write about what you learned. Send the link.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <BuildStageTile n="01" name="Sharpen" body="One sentence: this helps [specific user] do [specific thing] by [specific method]. If you cannot fill any of the three blanks, keep working the sentence." />
          <BuildStageTile n="02" name="Pick" body="Gemini Notebook if the tool helps someone learn. Perplexity Space if it answers an ongoing question. Claude Project if it does a task consistently. ElevenLabs Agent if people need to talk it through out loud." />
          <BuildStageTile n="03" name="Build v1" body="Load real sources or instructions. Run three real tasks. Run one edge case. Write down what happened, honestly." />
          <BuildStageTile n="04" name="Self-audit" body="Compare what happened to what you meant. Fix the gaps. Rerun the tasks. This is where the tool actually becomes good." />
          <BuildStageTile n="05" name="Ship + write" body="Send the link and a 600-1,000 word case study to one real reader today. A working link plus honest writing about the build." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The four platforms, at a glance
          </p>
          <p className="mt-3 text-text/90 leading-relaxed text-sm">
            <span className="font-mono text-[10px] text-amber mr-2">01</span>
            <span className="text-amber">Gemini Notebook</span> · Turn source material into a subject-matter expert. Best for helping someone learn something specific.
            <br />
            <span className="font-mono text-[10px] text-amber mr-2 mt-2 inline-block">02</span>
            <span className="text-amber">Perplexity Space</span> · A curated research assistant with custom instructions. Best for ongoing questions you keep answering.
            <br />
            <span className="font-mono text-[10px] text-amber mr-2 mt-2 inline-block">03</span>
            <span className="text-amber">Claude Project</span> · A custom AI teammate with knowledge loaded in. Best for a repeatable task you do often.
            <br />
            <span className="font-mono text-[10px] text-amber mr-2 mt-2 inline-block">04</span>
            <span className="text-amber">ElevenLabs Agent</span> · A voice agent people call from a link. Best for practice or help that works better spoken than typed.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          End of Week 6. You built something and sent it to a real reader.
          The next two weeks are about working with everyone else who also
          uses AI, and doing it in a way that raises the standard.
        </p>
      </div>
    </Section>
  );
}

function BuildStageTile({
  n,
  name,
  body,
}: {
  n: string;
  name: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] text-muted">{n}</span>
      </div>
      <div className="mt-2">
        <span className="inline-block rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Week 7
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Working with people who use AI differently than you
          </h3>
          <p className="mt-3 text-sm text-text/85">
            You just shipped something. Week 7 is about working in a room
            with people who use AI in ways you do not respect, and how to
            be the person on the team who raises the standard without
            slowing the team down.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The map',
    '1 · Problem + platform',
    '2 · Build v1',
    '3 · Self-audit',
    '4 · Case study + ship',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
